import * as React from 'react';
import { voiceNoteFileName } from '@/lib/utils/voice-note-file';
import { useTranslation } from '@/lib/i18n/react';
import { useAuthStore } from '@/store/auth-store';
import { sessionRevision, sessionScope } from '@/features/auth/session-scope';
import { formatNumber } from '@/lib/i18n/runtime';

interface VoiceNoteRecorderOptions {
  resourceId: string;
  onRecorded: (file: File) => void;
}

export interface VoiceNoteRecorder {
  isRecording: boolean;
  isStarting: boolean;
  duration: number;
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
}

interface RecordingAttempt {
  revision: number;
  resourceId: string;
  stream?: MediaStream;
  recorder?: MediaRecorder;
  chunks: Blob[];
  startedAt?: number;
  stopping?: boolean;
}

function releaseStream(stream?: MediaStream): void {
  stream?.getTracks().forEach((track) => track.stop());
}

/** Local recording only; upload and message delivery retain their own contracts. */
export function useVoiceNoteRecorder({ resourceId, onRecorded }: VoiceNoteRecorderOptions): VoiceNoteRecorder {
  const { t } = useTranslation('teamchat');
  const callbackRef = React.useRef(onRecorded);
  const resourceRef = React.useRef(resourceId);
  const mountedRef = React.useRef(false);
  const activeRef = React.useRef<RecordingAttempt | null>(null);
  const [isRecording, setIsRecording] = React.useState(false);
  const [isStarting, setIsStarting] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [errorKey, setErrorKey] = React.useState<string | null>(null);

  const current = React.useCallback((attempt: RecordingAttempt) =>
    mountedRef.current && activeRef.current === attempt &&
    attempt.revision === sessionRevision() && attempt.resourceId === resourceRef.current, []);

  const discard = React.useCallback(() => {
    const attempt = activeRef.current;
    activeRef.current = null;
    if (!attempt) return;
    const recorder = attempt.recorder;
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      if (recorder.state !== 'inactive') {
        try { recorder.stop(); } catch { /* Still release the owned stream. */ }
      }
    }
    releaseStream(attempt.stream);
    attempt.chunks = [];
  }, []);

  const reset = React.useCallback(() => {
    discard();
    setIsRecording(false);
    setIsStarting(false);
    setDuration(0);
    setErrorKey(null);
  }, [discard]);

  React.useLayoutEffect(() => {
    const resourceChanged = resourceRef.current !== resourceId;
    resourceRef.current = resourceId;
    callbackRef.current = onRecorded;
    if (resourceChanged) reset();
  }, [resourceId, onRecorded, reset]);

  React.useEffect(() => {
    mountedRef.current = true;
    // Invalidate synchronously even when React batches an A→B→A switch.
    const unsubscribe = useAuthStore.subscribe((next, previous) => {
      if (sessionScope(next) !== sessionScope(previous)) reset();
    });
    return () => {
      mountedRef.current = false;
      unsubscribe();
      discard();
    };
  }, [discard, reset]);

  React.useEffect(() => {
    if (!isRecording) return;
    const tick = window.setInterval(() => {
      const attempt = activeRef.current;
      if (attempt && current(attempt) && attempt.startedAt !== undefined) {
        setDuration(Math.floor((Date.now() - attempt.startedAt) / 1000));
      }
    }, 250);
    return () => window.clearInterval(tick);
  }, [isRecording, current]);

  const cancel = React.useCallback(() => reset(), [reset]);
  const stop = React.useCallback(() => {
    const attempt = activeRef.current;
    if (!attempt || !current(attempt) || attempt.stopping) return;
    if (!attempt.recorder) { reset(); return; }
    attempt.stopping = true;
    setIsRecording(false);
    try { attempt.recorder.stop(); } catch {
      discard();
      setErrorKey('voice.failed');
    }
  }, [current, reset, discard]);

  const start = React.useCallback(async () => {
    // One permission request or recorder at a time, including the stop event.
    if (!mountedRef.current || activeRef.current) return;
    setErrorKey(null);
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setErrorKey('voice.unsupported');
      return;
    }
    const attempt: RecordingAttempt = {
      revision: sessionRevision(), resourceId: resourceRef.current, chunks: [],
    };
    activeRef.current = attempt;
    setIsStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!current(attempt)) { releaseStream(stream); return; }
      attempt.stream = stream;
      const preferred = 'audio/webm;codecs=opus';
      const recorder = MediaRecorder.isTypeSupported(preferred)
        ? new MediaRecorder(stream, { mimeType: preferred }) : new MediaRecorder(stream);
      attempt.recorder = recorder;
      recorder.ondataavailable = (event) => {
        if (current(attempt) && event.data.size > 0) attempt.chunks.push(event.data);
      };
      recorder.onstop = () => {
        releaseStream(attempt.stream);
        if (!current(attempt)) return;
        activeRef.current = null;
        setIsRecording(false);
        setIsStarting(false);
        if (attempt.chunks.length === 0) return;
        const type = recorder.mimeType || 'audio/webm';
        const blob = new Blob(attempt.chunks, { type });
        attempt.chunks = [];
        callbackRef.current(new File([blob], voiceNoteFileName(type, Date.now()), { type }));
      };
      recorder.onerror = () => {
        if (!current(attempt)) return;
        discard();
        setIsRecording(false);
        setIsStarting(false);
        setErrorKey('voice.failed');
      };
      recorder.start();
      attempt.startedAt = Date.now();
      setDuration(0);
      setIsStarting(false);
      setIsRecording(true);
    } catch (cause) {
      if (!current(attempt)) return;
      discard();
      setIsStarting(false);
      setIsRecording(false);
      const permissionDenied = (cause instanceof DOMException || cause instanceof Error) && cause.name === 'NotAllowedError';
      setErrorKey(permissionDenied ? 'voice.denied' : 'voice.failed');
    }
  }, [current, discard]);

  return { isRecording, isStarting, duration, error: errorKey ? t(errorKey) : null, start, stop, cancel };
}

/** Numeric clock with a literal separator; invalid/native partial values show 0:00. */
export function formatVoiceNoteDuration(totalSeconds: number): string {
  const elapsed = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  return formatNumber(Math.floor(elapsed / 60), { useGrouping: false }) + ':' +
    formatNumber(elapsed % 60, { minimumIntegerDigits: 2, useGrouping: false });
}
